if (!customElements.get('media-gallery')) {
    customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        constructor() {
          super();
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
          this.mql = window.matchMedia('(min-width: 750px)');
          
          // Always set up slideChanged listener for viewer, even if no thumbnails
          if (this.elements.viewer) {
            this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          }
          
          // Only set up thumbnail interactions if thumbnails exist
          if (!this.elements.thumbnails) {
            // Still ensure is-active is set on viewer slides
            this.ensureViewerActiveSlide();
            return;
          }
          
          if (this.elements.thumbnails) {
            this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
              mediaToSwitch
                .querySelector('button')
                .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
            });
          }
          
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

          // Ensure an initial active media + thumbnail is set on load.
          const activeMedia = this.elements.viewer?.querySelector('[data-media-id].is-active');
          if (!activeMedia) {
            const firstThumbnail = this.elements.thumbnails?.querySelector('[data-target]');
            if (firstThumbnail?.dataset?.target) {
              this.setActiveMedia(firstThumbnail.dataset.target, false);
            } else {
              const firstMedia = this.elements.viewer?.querySelector('[data-media-id]');
              if (firstMedia) {
                firstMedia.classList.add('is-active');
                if (this.elements.thumbnails) {
                  const matchingThumbnail = this.elements.thumbnails?.querySelector(
                    `[data-target="${firstMedia.dataset.mediaId}"]`
                  );
                  this.setActiveThumbnail(matchingThumbnail);
                }
              }
            }
          } else {
            if (this.elements.thumbnails) {
              const matchingThumbnail = this.elements.thumbnails?.querySelector(
                `[data-target="${activeMedia.dataset.mediaId}"]`
              );
              this.setActiveThumbnail(matchingThumbnail);
            }
          }
          
          // Ensure is-active is maintained when slider component updates
          // This handles cases where slider component dispatches slideChanged events
          // The onSlideChanged handler will update is-active, but we also ensure it's set initially
          const ensureActiveExists = () => {
            const hasActive = this.elements.viewer?.querySelector('[data-media-id].is-active');
            if (!hasActive) {
              const firstMedia = this.elements.viewer?.querySelector('[data-media-id]');
              if (firstMedia) {
                firstMedia.classList.add('is-active');
              }
            }
          };
          
          // Check after slider component has had time to initialize
          setTimeout(ensureActiveExists, 200);
        }
  
        onSlideChanged(event) {
          const currentElement = event.detail.currentElement;
          if (!currentElement) return;
          
          // Update is-active class on slides
          const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
          mediaItems.forEach((element) => {
            element.classList.remove('is-active');
          });
          currentElement.classList.add('is-active');
          
          // Update thumbnail
          if (this.elements.thumbnails) {
            const thumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${currentElement.dataset.mediaId}"]`
            );
            this.setActiveThumbnail(thumbnail);
          }
          
          // Ensure deferred media loads when slide changes via slider
          this.playActiveMedia(currentElement);
        }
  
        setActiveMedia(mediaId, prepend) {
          const activeMedia =
            this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
            this.elements.viewer.querySelector('[data-media-id]');
          if (!activeMedia) {
            return;
          }
          const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
          mediaItems.forEach((element) => {
            element.classList.remove('is-active');
          });
          activeMedia?.classList?.add('is-active');
  
          if (prepend) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
  
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
  
            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }
  
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
          this.playActiveMedia(activeMedia);
  
          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
  
        setActiveThumbnail(thumbnail) {
          if (!this.elements.thumbnails || !thumbnail) return;
  
          this.elements.thumbnails
            .querySelectorAll('button')
            .forEach((element) => element.removeAttribute('aria-current'));
          thumbnail.querySelector('button').setAttribute('aria-current', true);
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
  
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
  
        announceLiveRegion(activeItem, position) {
          const image = activeItem.querySelector('.product__modal-opener--image img');
          if (!image) return;
          image.onload = () => {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
            setTimeout(() => {
              this.elements.liveRegion.setAttribute('aria-hidden', true);
            }, 2000);
          };
          image.src = image.src;
        }
  
        playActiveMedia(activeItem) {
          window.pauseAllMedia();
          const deferredMedia = activeItem.querySelector('deferred-media, .deferred-media');
          if (deferredMedia) deferredMedia.loadContent(false);
        }
  
        preventStickyHeader() {
          this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
          if (!this.stickyHeader) return;
          this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }
  
        removeListSemantic() {
          if (!this.elements.viewer.slider) return;
          this.elements.viewer.slider.setAttribute('role', 'presentation');
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
        
        ensureViewerActiveSlide() {
          if (!this.elements.viewer) return;
          const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
          if (!activeMedia) {
            const firstMedia = this.elements.viewer.querySelector('[data-media-id]');
            if (firstMedia) {
              firstMedia.classList.add('is-active');
            }
          }
        }
      }
    );
  }
  